<template>
  <div
    v-bind="$attrs"
    v-on="$listeners"
    :style="style.root"
  >
    <div
      :disabled="disabled"
      :freeze="freeze"
      :class="$style.container"
      :style="style.container"
    >
      <template v-for="(option, index) in options">
        <div
          :key="index"
          :disabled="disabled"
          :freeze="freeze"
          :selected="selectedIndex === index"
          @mousedown="selectOption(index)"
          @mouseup="selectedOption"
          :class="(index === 0)
            ? $style.buttonFirst
            : ((index === options.length - 1)
              ? $style.buttonLast
              : $style.button)"
          :style="(index === 0)
            ? style.buttonFirst
            : ((index === options.length - 1)
              ? style.buttonLast
              : style.button)"
        >
          <slot
            name="option"
            :disabled="disabled"
            :freeze="freeze"
            :index="index"
            :option="option"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import { CssStyleProcessor } from '@js/vue-style-utils'
  import { KeyError } from '@js/exceptions'

  export default {
    name: 'BaseMultiToggle',
    inheritAttrs: false,
    model: {
      prop: 'vmodelValue',
      event: 'change',
    },
    props: {
      vmodelValue: {
        type: [Number, Object, String], // index or value of specified as id
        required: false,
        default: null,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false,
      },
      disableDeselect: {
        type: Boolean,
        required: false,
        default: false,
      },
      /**
      * disabled is set to true if the interaction is disallowed
      * AND the components are displayed to indicate the state.
      * freeze is set to true in case that the interaction is disallowed
      * BUT the components are displayed not to indicate the state.
      **/
      freeze: {
        type: Boolean,
        required: false,
        default: false,
      },
      idKey: {
        type: String,
        required: false,
        default: 'id',
      },
      maxWidthButton: {
        type: String,
        required: false,
        default: 'none',
      },
      minWidthButton: {
        type: String,
        required: false,
        default: 'none',
      },
      options: {
        type: Array,
        required: false,
        default: () => [],
      },
      styleConfig: {
        type: Object,
        required: false,
        default: () => {},
      },
      truncate: {
        type: Boolean,
        required: false,
        default: false,
      },
    },
    data: function () {
      return {
        selectedIndex: this.getSelectedIndex(this.vmodelValue),
        selectedOptionId: null,
        style: {
          button: {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            maxWidth: this.maxWidthButton,
            minWidth: this.minWidthButton,
            whiteSpace: 'nowrap',
            ...(this.truncate ? {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            } : {}),
          },
          buttonFirst: {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            maxWidth: this.maxWidthButton,
            minWidth: this.minWidthButton,
            whiteSpace: 'nowrap',
            ...(this.truncate ? {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            } : {}),
          },
          buttonLast: {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            justifyContent: 'center',
            maxWidth: this.maxWidthButton,
            minWidth: this.minWidthButton,
            whiteSpace: 'nowrap',
            ...(this.truncate ? {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            } : {}),
          },
          container: {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
          },
          root: {},
        },
      };
    },
    created: function () {
      this.selectedOptionId = this.selectedIndex === null
        ? null
        : this.options[this.selectedIndex][this.idKey];

      let proc = new CssStyleProcessor(
        '--v2-base-multi-toggle',
        ['freeze', 'selected', 'selected-hover', 'selected-disabled'],
      );

      Object.assign(
        this.style.button,
        proc.getExtendedStyle(this.styleConfig.button, 'button'),
      );
      Object.assign(
        this.style.buttonFirst,
        proc.getExtendedStyle(this.styleConfig.buttonFirst, 'buttonFirst'),
      );
      Object.assign(
        this.style.buttonLast,
        proc.getExtendedStyle(this.styleConfig.buttonLast, 'buttonLast'),
      );

      proc = new CssStyleProcessor('--v2-base-multi-toggle');
      Object.assign(
        this.style.container,
        proc.getExtendedStyle(this.styleConfig.container, 'container'),
      );
    },
    methods: {
      getSelectedIndex: function (vmodelValue) {
        this.validateIdKey();
        const index = this.options
          .findIndex(elem => elem[this.idKey] === vmodelValue);
        return index < 0 ? null : index;
      },
      selectedOption: function () {
        if (this.disabled || this.freeze) {
          return;
        }

        this.$emit('select', this.selectedOptionId);
      },
      selectOption: function (index) {
        if (this.disabled || this.freeze) {
          return;
        }

        this.selectedIndex = (
          !this.disableDeselect && this.selectedIndex === index)
            ? null
            : index;

        this.selectedOptionId = this.selectedIndex === null
          ? null
          : this.options[this.selectedIndex][this.idKey];
        //this.$emit('change', selectedOptionId);
      },
      validateIdKey: function () {
        if (!this.options.every(obj => this.idKey in obj)) {
          throw new KeyError('The specified idKey not found in given options.');
        }
      },
    },
    watch: {
      vmodelValue: function (newVal, oldVal) {
        this.selectedIndex = this.getSelectedIndex(newVal);
      },
      idKey: function (newVal, oldVal) {
        this.validateIdKey(newVal);
      },
      selectedOptionId: function (newVal, oldVal) {
        this.$emit('change', newVal);
      },
    },
  };
</script>

<style lang="stylus" module>
  .button
    background-color var(--v2-base-multi-toggle-button-background-color)
    border var(--v2-base-multi-toggle-button-border)
    border-radius var(--v2-base-multi-toggle-button-border-radius)
    border-width var(--v2-base-multi-toggle-button-border-width)
    box-shadow var(--v2-base-multi-toggle-button-box-shadow)
    color var(--v2-base-multi-toggle-button-color)
    cursor var(--v2-base-multi-toggle-button-cursor)
    font-family var(--v2-base-multi-toggle-button-font-family)
    font-size var(--v2-base-multi-toggle-button-font-size)
    font-weight var(--v2-base-multi-toggle-button-font-weight)
    text-decoration var(--v2-base-multi-toggle-button-text-decoration)
    &[selected], &[selected]:hover
      background-color var(--v2-base-multi-toggle-button-background-color-selected)
      border var(--v2-base-multi-toggle-button-border-selected)
      border-radius var(--v2-base-multi-toggle-button-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-color-selected)
      cursor var(--v2-base-multi-toggle-button-cursor-selected)
      font-family var(--v2-base-multi-toggle-button-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-text-decoration-selected)
    &:hover
      background-color var(--v2-base-multi-toggle-button-background-color-hover)
      border var(--v2-base-multi-toggle-button-border-hover)
      border-radius var(--v2-base-multi-toggle-button-border-radius-hover)
      border-width var(--v2-base-multi-toggle-button-border-width-hover)
      box-shadow var(--v2-base-multi-toggle-button-box-shadow-hover)
      color var(--v2-base-multi-toggle-button-color-hover)
      cursor var(--v2-base-multi-toggle-button-cursor-hover)
      font-family var(--v2-base-multi-toggle-button-font-family-hover)
      font-size var(--v2-base-multi-toggle-button-font-size-hover)
      font-weight var(--v2-base-multi-toggle-button-font-weight-hover)
      text-decoration var(--v2-base-multi-toggle-button-text-decoration-hover)
    &[disabled]
      background-color var(--v2-base-multi-toggle-button-background-color-disabled)
      border var(--v2-base-multi-toggle-button-border-disabled)
      border-radius var(--v2-base-multi-toggle-button-border-radius-disabled)
      border-width var(--v2-base-multi-toggle-button-border-width-disabled)
      box-shadow var(--v2-base-multi-toggle-button-box-shadow-disabled)
      color var(--v2-base-multi-toggle-button-color-disabled)
      cursor var(--v2-base-multi-toggle-button-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-font-family-disabled)
      font-size var(--v2-base-multi-toggle-button-font-size-disabled)
      font-weight var(--v2-base-multi-toggle-button-font-weight-disabled)
      text-decoration var(--v2-base-multi-toggle-button-text-decoration-disabled)
    &[freeze], &[freeze]:hover
      background-color var(--v2-base-multi-toggle-button-background-color)
      border var(--v2-base-multi-toggle-button-border)
      border-radius var(--v2-base-multi-toggle-button-border-radius)
      border-width var(--v2-base-multi-toggle-button-border-width)
      box-shadow var(--v2-base-multi-toggle-button-box-shadow)
      color var(--v2-base-multi-toggle-button-color)
      cursor var(--v2-base-multi-toggle-button-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-font-family)
      font-size var(--v2-base-multi-toggle-button-font-size)
      font-weight var(--v2-base-multi-toggle-button-font-weight)
      text-decoration var(--v2-base-multi-toggle-button-text-decoration)
    &[freeze][selected]
      background-color var(--v2-base-multi-toggle-button-background-color-selected)
      border var(--v2-base-multi-toggle-button-border-selected)
      border-radius var(--v2-base-multi-toggle-button-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-color-selected)
      cursor var(--v2-base-multi-toggle-button-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-text-decoration-selected)

  .buttonFirst
    background-color var(--v2-base-multi-toggle-button-first-background-color)
    border var(--v2-base-multi-toggle-button-first-border)
    border-radius var(--v2-base-multi-toggle-button-first-border-radius)
    border-width var(--v2-base-multi-toggle-button-first-border-width)
    box-shadow var(--v2-base-multi-toggle-button-first-box-shadow)
    color var(--v2-base-multi-toggle-button-first-color)
    cursor var(--v2-base-multi-toggle-button-first-cursor)
    font-family var(--v2-base-multi-toggle-button-first-font-family)
    font-size var(--v2-base-multi-toggle-button-first-font-size)
    font-weight var(--v2-base-multi-toggle-button-first-font-weight)
    text-decoration var(--v2-base-multi-toggle-button-first-text-decoration)
    &[selected], &[selected]:hover
      background-color var(--v2-base-multi-toggle-button-first-background-color-selected)
      border var(--v2-base-multi-toggle-button-first-border-selected)
      border-radius var(--v2-base-multi-toggle-button-first-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-first-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-first-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-first-color-selected)
      cursor var(--v2-base-multi-toggle-button-first-cursor-selected)
      font-family var(--v2-base-multi-toggle-button-first-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-first-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-first-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-first-text-decoration-selected)
    &:hover
      background-color var(--v2-base-multi-toggle-button-first-background-color-hover)
      border var(--v2-base-multi-toggle-button-first-border-hover)
      border-radius var(--v2-base-multi-toggle-button-first-border-radius-hover)
      border-width var(--v2-base-multi-toggle-button-first-border-width-hover)
      box-shadow var(--v2-base-multi-toggle-button-first-box-shadow-hover)
      color var(--v2-base-multi-toggle-button-first-color-hover)
      cursor var(--v2-base-multi-toggle-button-first-cursor-hover)
      font-family var(--v2-base-multi-toggle-button-first-font-family-hover)
      font-size var(--v2-base-multi-toggle-button-first-font-size-hover)
      font-weight var(--v2-base-multi-toggle-button-first-font-weight-hover)
      text-decoration var(--v2-base-multi-toggle-button-first-text-decoration-hover)
    &[disabled]
      background-color var(--v2-base-multi-toggle-button-first-background-color-disabled)
      border var(--v2-base-multi-toggle-button-first-border-disabled)
      border-radius var(--v2-base-multi-toggle-button-first-border-radius-disabled)
      border-width var(--v2-base-multi-toggle-button-first-border-width-disabled)
      box-shadow var(--v2-base-multi-toggle-button-first-box-shadow-disabled)
      color var(--v2-base-multi-toggle-button-first-color-disabled)
      cursor var(--v2-base-multi-toggle-button-first-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-first-font-family-disabled)
      font-size var(--v2-base-multi-toggle-button-first-font-size-disabled)
      font-weight var(--v2-base-multi-toggle-button-first-font-weight-disabled)
      text-decoration var(--v2-base-multi-toggle-button-first-text-decoration-disabled)
    &[freeze], &[freeze]:hover
      background-color var(--v2-base-multi-toggle-button-first-background-color)
      border var(--v2-base-multi-toggle-button-first-border)
      border-radius var(--v2-base-multi-toggle-button-first-border-radius)
      border-width var(--v2-base-multi-toggle-button-first-border-width)
      box-shadow var(--v2-base-multi-toggle-button-first-box-shadow)
      color var(--v2-base-multi-toggle-button-first-color)
      cursor var(--v2-base-multi-toggle-button-first-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-first-font-family)
      font-size var(--v2-base-multi-toggle-button-first-font-size)
      font-weight var(--v2-base-multi-toggle-button-first-font-weight)
      text-decoration var(--v2-base-multi-toggle-button-first-text-decoration)
    &[freeze][selected]
      background-color var(--v2-base-multi-toggle-button-first-background-color-selected)
      border var(--v2-base-multi-toggle-button-first-border-selected)
      border-radius var(--v2-base-multi-toggle-button-first-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-first-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-first-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-first-color-selected)
      cursor var(--v2-base-multi-toggle-button-first-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-first-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-first-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-first-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-first-text-decoration-selected)

  .buttonLast
    background-color var(--v2-base-multi-toggle-button-last-background-color)
    border var(--v2-base-multi-toggle-button-last-border)
    border-radius var(--v2-base-multi-toggle-button-last-border-radius)
    border-width var(--v2-base-multi-toggle-button-last-border-width)
    box-shadow var(--v2-base-multi-toggle-button-last-box-shadow)
    color var(--v2-base-multi-toggle-button-last-color)
    cursor var(--v2-base-multi-toggle-button-last-cursor)
    font-family var(--v2-base-multi-toggle-button-last-font-family)
    font-size var(--v2-base-multi-toggle-button-last-font-size)
    font-weight var(--v2-base-multi-toggle-button-last-font-weight)
    text-decoration var(--v2-base-multi-toggle-button-last-text-decoration)
    &[selected], &[selected]:hover
      background-color var(--v2-base-multi-toggle-button-last-background-color-selected)
      border var(--v2-base-multi-toggle-button-last-border-selected)
      border-radius var(--v2-base-multi-toggle-button-last-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-last-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-last-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-last-color-selected)
      cursor var(--v2-base-multi-toggle-button-last-cursor-selected)
      font-family var(--v2-base-multi-toggle-button-last-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-last-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-last-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-last-text-decoration-selected)
    &:hover
      background-color var(--v2-base-multi-toggle-button-last-background-color-hover)
      border var(--v2-base-multi-toggle-button-last-border-hover)
      border-radius var(--v2-base-multi-toggle-button-last-border-radius-hover)
      border-width var(--v2-base-multi-toggle-button-last-border-width-hover)
      box-shadow var(--v2-base-multi-toggle-button-last-box-shadow-hover)
      color var(--v2-base-multi-toggle-button-last-color-hover)
      cursor var(--v2-base-multi-toggle-button-last-cursor-hover)
      font-family var(--v2-base-multi-toggle-button-last-font-family-hover)
      font-size var(--v2-base-multi-toggle-button-last-font-size-hover)
      font-weight var(--v2-base-multi-toggle-button-last-font-weight-hover)
      text-decoration var(--v2-base-multi-toggle-button-last-text-decoration-hover)
    &[disabled]
      background-color var(--v2-base-multi-toggle-button-last-background-color-disabled)
      border var(--v2-base-multi-toggle-button-last-border-disabled)
      border-radius var(--v2-base-multi-toggle-button-last-border-radius-disabled)
      border-width var(--v2-base-multi-toggle-button-last-border-width-disabled)
      box-shadow var(--v2-base-multi-toggle-button-last-box-shadow-disabled)
      color var(--v2-base-multi-toggle-button-last-color-disabled)
      cursor var(--v2-base-multi-toggle-button-last-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-last-font-family-disabled)
      font-size var(--v2-base-multi-toggle-button-last-font-size-disabled)
      font-weight var(--v2-base-multi-toggle-button-last-font-weight-disabled)
      text-decoration var(--v2-base-multi-toggle-button-last-text-decoration-disabled)
    &[freeze], &[freeze]:hover
      background-color var(--v2-base-multi-toggle-button-last-background-color)
      border var(--v2-base-multi-toggle-button-last-border)
      border-radius var(--v2-base-multi-toggle-button-last-border-radius)
      border-width var(--v2-base-multi-toggle-button-last-border-width)
      box-shadow var(--v2-base-multi-toggle-button-last-box-shadow)
      color var(--v2-base-multi-toggle-button-last-color)
      cursor var(--v2-base-multi-toggle-button-last-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-last-font-family)
      font-size var(--v2-base-multi-toggle-button-last-font-size)
      font-weight var(--v2-base-multi-toggle-button-last-font-weight)
      text-decoration var(--v2-base-multi-toggle-button-last-text-decoration)
    &[freeze][selected]
      background-color var(--v2-base-multi-toggle-button-last-background-color-selected)
      border var(--v2-base-multi-toggle-button-last-border-selected)
      border-radius var(--v2-base-multi-toggle-button-last-border-radius-selected)
      border-width var(--v2-base-multi-toggle-button-last-border-width-selected)
      box-shadow var(--v2-base-multi-toggle-button-last-box-shadow-selected)
      color var(--v2-base-multi-toggle-button-last-color-selected)
      cursor var(--v2-base-multi-toggle-button-last-cursor-disabled)
      font-family var(--v2-base-multi-toggle-button-last-font-family-selected)
      font-size var(--v2-base-multi-toggle-button-last-font-size-selected)
      font-weight var(--v2-base-multi-toggle-button-last-font-weight-selected)
      text-decoration var(--v2-base-multi-toggle-button-last-text-decoration-selected)

  .container
    background-color var(--v2-base-multi-toggle-container-background-color)
    border var(--v2-base-multi-toggle-container-border)
    border-radius var(--v2-base-multi-toggle-container-border-radius)
    border-width var(--v2-base-multi-toggle-container-border-width)
    box-shadow var(--v2-base-multi-toggle-container-box-shadow)
    &:hover
      background-color var(--v2-base-multi-toggle-container-background-color-hover)
      border var(--v2-base-multi-toggle-container-border-hover)
      border-radius var(--v2-base-multi-toggle-container-border-radius-hover)
      border-width var(--v2-base-multi-toggle-container-border-width-hover)
      box-shadow var(--v2-base-multi-toggle-container-box-shadow-hover)
    &[disabled]
      background-color var(--v2-base-multi-toggle-container-background-color-disabled)
      border var(--v2-base-multi-toggle-container-border-disabled)
      border-radius var(--v2-base-multi-toggle-container-border-radius-disabled)
      border-width var(--v2-base-multi-toggle-container-border-width-disabled)
      box-shadow var(--v2-base-multi-toggle-container-box-shadow-disabled)
</style>
